'use client';

import { createContext, useContext, useMemo, ReactNode } from 'react';

interface IConsentContextValue {
    hasConsent: boolean;
    isConsentRequired: boolean;
    shouldShowAlert: boolean;
    isAlertShaking: boolean;
    /**
     * Whether interactive login affordances should be considered "blocked"
     * by the consent gate. Equivalent to `isConsentRequired && !hasConsent`.
     */
    isLoginBlockedByConsent: boolean;
    isConsentV2: boolean;
    isLoginDisabledByConsent: boolean;
    handleConsentChange: (isConsented: boolean) => void;
    /**
     * Invoked when a login affordance is activated while consent is required
     * but not yet granted. Surfaces / shakes the terms alert.
     */
    handleOverlayClick: () => void;
}

const ConsentContext = createContext<IConsentContextValue | undefined>(undefined);

interface IConsentProviderProps {
    value: Omit<IConsentContextValue, 'isLoginBlockedByConsent' | 'isLoginDisabledByConsent'>;
    children: ReactNode;
}

export function ConsentProvider({ value, children }: IConsentProviderProps): React.ReactElement {
    const contextValue = useMemo<IConsentContextValue>(() => ({
        ...value,
        isLoginBlockedByConsent: value.isConsentRequired && !value.hasConsent,
        isLoginDisabledByConsent:
            value.isConsentV2 && value.isConsentRequired && !value.hasConsent,
    }), [value]);

    return (
        <ConsentContext.Provider value={contextValue}>
            {children}
        </ConsentContext.Provider>
    );
}

/**
 * Read the consent state inside the login subtree. When called outside the
 * provider (e.g. in unrelated routes) it falls back to "no consent gate" so
 * existing flows continue to work unchanged.
 */
export function useConsentContext(): IConsentContextValue {
    const ctx = useContext(ConsentContext);
    if (!ctx) {
        return {
            hasConsent: true,
            isConsentRequired: false,
            shouldShowAlert: false,
            isAlertShaking: false,
            isLoginBlockedByConsent: false,
            isConsentV2: false,
            isLoginDisabledByConsent: false,
            handleConsentChange: () => { },
            handleOverlayClick: () => { },
        };
    }
    return ctx;
}
